import { useCallback, useEffect, useState } from 'react';
import { BACKOFFICE_TOOL_ROUTES } from '../utils/backoffice/backoffice-tool-routes';
import {
  EMPTY_TOOL_PREFS,
  TOOL_PREFS_CHANGED_EVENT,
  TOOL_PREFS_STORAGE_KEY,
  loadToolPrefs,
  recordToolVisit,
  saveToolPrefs,
  toggleToolPin,
  type BackofficeToolPrefs,
} from '../utils/backoffice/backoffice-tool-prefs';

const VALID_PATHS: ReadonlySet<string> = new Set(BACKOFFICE_TOOL_ROUTES.map((tool) => tool.path));

/**
 * Pinned and recent backoffice tools for this browser. Starts empty (so server and client render
 * the same HTML), loads from localStorage after mount, and stays in sync across the nav, the
 * picker, the hub page and other tabs.
 */
export function useBackofficeToolPrefs() {
  const [prefs, setPrefs] = useState<BackofficeToolPrefs>(EMPTY_TOOL_PREFS);

  useEffect(() => {
    const reload = () => setPrefs(loadToolPrefs(VALID_PATHS));
    reload();
    const onStorage = (event: StorageEvent) => {
      if (event.key === TOOL_PREFS_STORAGE_KEY) reload();
    };
    window.addEventListener(TOOL_PREFS_CHANGED_EVENT, reload);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(TOOL_PREFS_CHANGED_EVENT, reload);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const togglePin = useCallback((path: string) => {
    saveToolPrefs(toggleToolPin(loadToolPrefs(VALID_PATHS), path));
  }, []);

  const recordVisit = useCallback((path: string) => {
    const current = loadToolPrefs(VALID_PATHS);
    const next = recordToolVisit(current, path);
    if (next !== current) saveToolPrefs(next);
  }, []);

  return { prefs, togglePin, recordVisit };
}
